import { useState } from 'react';

/**
 * The download dialog.
 *
 * Short on purpose: the dialog is small, and the two things worth saying are
 * the ones that cost people a re-export — that "this page" is not the whole
 * sheet, and that hidden columns are dropped unless you ask for them.
 *
 * Which controls exist depends on the sheet: the scope picker is rendered only
 * where the sheet paginates, and the hidden-columns tick only where there are
 * hidden columns to include. So the steps are filtered against the DOM on
 * start rather than relying on the target_not_found branch — a run whose first
 * few targets are all absent never gets off the ground.
 */
const ALL_STEPS = [
    {
        target: '#export-scope-page',
        title: 'Just this page',
        content: 'Exports the rows currently on screen, with whatever filters and sorting you have applied. Fastest, and usually what you want.',
        disableBeacon: true,
    },
    {
        target: '#export-scope-all',
        title: 'Every matching record',
        content: 'Fetches every record that matches your filters, not just the page. Greyed out above the row cap, where a spreadsheet stops being the right tool.',
        disableBeacon: true,
    },
    {
        target: '#export-include-hidden',
        title: 'Hidden columns',
        content: 'Columns you have hidden on the sheet are left out by default. Tick this to include them — and note that most of the calculated columns only come out as live formulas when it is ticked.',
        disableBeacon: true,
    },
    {
        target: '#export-filename',
        title: 'Name the file',
        content: 'Pre-filled with the sheet name and today\'s date. Change it if you are sending it on to someone.',
        disableBeacon: true,
    },
    {
        target: '#export-confirm',
        title: 'Download',
        content: 'Totals and derived columns come out as real Excel formulas, so the sheet still recalculates if you edit it. A large export shows progress here and can be cancelled.',
        disableBeacon: true,
    },
];

function useExportDialogTour() {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [steps, setSteps] = useState(ALL_STEPS);

    const handleStartTutorial = () => {
        setRunTour(false);
        setStepIndex(0);
        setTimeout(() => {
            const present = ALL_STEPS.filter(step => document.querySelector(step.target));
            if (!present.length) return;
            setSteps(present);
            setStepIndex(0);
            setRunTour(true);
        }, 100);
    };

    const handleJoyrideCallback = (data) => {
        const { status, action, type, index, origin } = data;

        if (origin === 'overlay' && action === 'close') return;

        if (type === 'error:target_not_found' || status === 'error') {
            if (index + 1 < steps.length) {
                setStepIndex(index + 1);
                return;
            }
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (status === 'finished' || status === 'skipped' || action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            if (index - 1 < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
            setStepIndex(index - 1);
        }
    };

    return { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback };
}

export default useExportDialogTour;
